import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useBatch } from '@/contexts/BatchContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/ui/use-toast';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { FormFieldError } from '@/components/ui/form-error';
import { Alert } from '@/components/ui/alert';
import { Student, Class } from '@/types';
import { studentApi, classApi } from '@/lib/api';

interface StudentFormProps {
  student?: Student;
  isEditing?: boolean;
}

const StudentForm = ({ student, isEditing = false }: StudentFormProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { currentBatch, batches } = useBatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableClasses, setAvailableClasses] = useState<Class[]>([]);
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>(student?.classIds || []);
  const { handleError, clearError, isError, error: errorInfo } = useErrorHandler({
    showToast: false,
    scrollToError: true,
    autoFocus: true
  });

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<Student>({
    defaultValues: student || {
      firstName: '',
      lastName: '',
      email: '',
      studentId: '',
      enrollmentDate: new Date().toISOString().split('T')[0],
      status: 'active',
      batchId: currentBatch?.id || '',
      classIds: [],
    }
  });

  useEffect(() => {
    if (student) {
      // Set form values from student data
      setValue('firstName', student.firstName);
      setValue('lastName', student.lastName);
      setValue('email', student.email);
      setValue('studentId', student.studentId);
      setValue('enrollmentDate', student.enrollmentDate);
      setValue('status', student.status);
      setValue('batchId', student.batchId || student.academicYearId); // Handle both field names for backward compatibility
      setValue('classIds', student.classIds);
      setSelectedClassIds(student.classIds || []);
    }
  }, [student, setValue]);

  // Load classes for the current batch
  useEffect(() => {
    const loadClasses = async () => {
      if (currentBatch) {
        try {
          const classesData = await classApi.getAll();
          // Handle both paginated and direct array responses
          const classes = Array.isArray(classesData) ? classesData : ((classesData as any)?.content || []);
          setAvailableClasses(classes);
        } catch (error) {
          console.error('Failed to load classes:', error);
          const errorInfo = handleError(error, 'Failed to load classes. Please try again.');
          toast({
            title: errorInfo.title,
            description: errorInfo.message,
            variant: 'destructive',
          });
        }
      }
    };

    loadClasses();
  }, [currentBatch, toast]);

  const onSubmit = async (data: Student) => {
    try {
      setIsSubmitting(true);

      // Include selected class IDs in the data
      const studentData = {
        ...data,
        classIds: selectedClassIds
      };

      if (isEditing && student?.id) {
        // Update existing student
        const updatedStudent = await studentApi.update(student.id, studentData);
        console.log('Student updated successfully:', updatedStudent);
        toast({
          title: 'Success',
          description: 'Student updated successfully',
        });
      } else {
        // Create new student
        const newStudent = await studentApi.create(studentData);
        console.log('Student created successfully:', newStudent);
        toast({
          title: 'Success',
          description: 'Student created successfully',
        });
      }

      // Navigate back to students list with a refresh parameter
      navigate('/students?refresh=true');
    } catch (error) {
      console.error('Error saving student:', error);
      const errorInfo = handleError(error, 'Failed to save student. Please try again.');
      toast({
        title: errorInfo.title,
        description: errorInfo.message,
        variant: 'destructive',
        scrollToView: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Global Error Display */}
      {isError && errorInfo && (
        <Alert
          variant="destructive"
          title={errorInfo.title}
          description={errorInfo.message}
          scrollToView={true}
          autoFocus={true}
          dismissible={true}
          onDismiss={clearError}
          id="student-form-error"
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="firstName">First Name</Label>
          <Input
            id="firstName"
            {...register('firstName', { required: 'First name is required' })}
          />
          <FormFieldError
            error={errors.firstName}
            fieldName="firstName"
            scrollToView={true}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastName">Last Name</Label>
          <Input
            id="lastName"
            {...register('lastName', { required: 'Last name is required' })}
          />
          <FormFieldError
            error={errors.lastName}
            fieldName="lastName"
            scrollToView={true}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              }
            })}
          />
          <FormFieldError
            error={errors.email}
            fieldName="email"
            scrollToView={true}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="studentId">Student ID</Label>
          <Input
            id="studentId"
            {...register('studentId', { required: 'Student ID is required' })}
          />
          <FormFieldError
            error={errors.studentId}
            fieldName="studentId"
            scrollToView={true}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="enrollmentDate">Enrollment Date</Label>
          <Input
            id="enrollmentDate"
            type="date"
            {...register('enrollmentDate', { required: 'Enrollment date is required' })}
          />
          <FormFieldError
            error={errors.enrollmentDate}
            fieldName="enrollmentDate"
            scrollToView={true}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select
            defaultValue={student?.status || 'active'}
            onValueChange={(value) => setValue('status', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="graduated">Graduated</SelectItem>
              <SelectItem value="transferred">Transferred</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
            </SelectContent>
          </Select>
          <FormFieldError
            error={errors.status}
            fieldName="status"
            scrollToView={true}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="batchId">Batch</Label>
          <Select
            defaultValue={student?.batchId || student?.academicYearId || currentBatch?.id}
            onValueChange={(value) => setValue('batchId', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select batch" />
            </SelectTrigger>
            <SelectContent>
              {batches.map((batch) => (
                <SelectItem key={batch.id} value={batch.id}>
                  {batch.year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormFieldError
            error={errors.batchId}
            fieldName="batchId"
            scrollToView={true}
          />
        </div>
      </div>

      {/* Multiple Class Selection */}
      <div className="space-y-2">
        <Label>Classes (Optional)</Label>
        <p className="text-sm text-muted-foreground">
          Select one or more classes for this student
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-48 overflow-y-auto border rounded-md p-3">
          {availableClasses.length === 0 ? (
            <p className="text-sm text-muted-foreground col-span-full">
              No classes available for the current batch
            </p>
          ) : (
            availableClasses.map((classItem) => (
              <div key={classItem.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`class-${classItem.id}`}
                  checked={selectedClassIds.includes(classItem.id)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedClassIds(prev => [...prev, classItem.id]);
                    } else {
                      setSelectedClassIds(prev => prev.filter(id => id !== classItem.id));
                    }
                  }}
                />
                <Label
                  htmlFor={`class-${classItem.id}`}
                  className="text-sm font-normal cursor-pointer"
                >
                  {classItem.name}
                </Label>
              </div>
            ))
          )}
        </div>
        {selectedClassIds.length > 0 && (
          <p className="text-sm text-muted-foreground">
            Selected {selectedClassIds.length} class{selectedClassIds.length !== 1 ? 'es' : ''}
          </p>
        )}
      </div>

      <div className="flex justify-end space-x-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/students')}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (isEditing ? 'Updating student...' : 'Creating student...') : isEditing ? 'Update Student' : 'Create Student'}
        </Button>
      </div>
    </form>
  );
};

export default StudentForm;
